import React, { useState, useRef } from "react";
import Icon from "../../../components/AppIcon";

const CsvImportModal = ({ isOpen, onClose, onImport, loading = false }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importStats, setImportStats] = useState(null);
  const fileInputRef = useRef(null);

  const expectedHeaders = [
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "jobTitle",
    "department",
    "location",
    "contractedHoursPerWeek",
    "hourlyRate",
    "payrollNumber",
    "isEligibleForTronc",
    "startDate",
  ];

  const parseCSV = (text) => {
    // Strip UTF-8 BOM if present (common when saving from Excel on Windows)
    const cleanText = text.replace(/^\uFEFF/, "");
    const lines = cleanText.split("\n").filter((line) => line.trim());
    if (lines.length < 2) {
      return {
        headers: [],
        rows: [],
        errors: ["CSV file must have headers and at least one data row"],
      };
    }

    const headers = lines[0]
      .split(",")
      .map((h) => h.trim().replace(/^["']|["']$/g, ""));
    const rows = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i]
        .split(",")
        .map((v) => v.trim().replace(/^["']|["']$/g, ""));

      if (values.length !== headers.length) {
        parseErrors.push(
          `Row ${i}: Column count mismatch (expected ${headers.length}, got ${values.length})`,
        );
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });

      // Validate required fields
      if (!row.firstName || !row.lastName || !row.email) {
        parseErrors.push(
          `Row ${i}: Missing required fields (firstName, lastName, email)`,
        );
        continue;
      }

      // Validate email format
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
        parseErrors.push(`Row ${i}: Invalid email format (${row.email})`);
        continue;
      }

      // Transform data
      rows.push({
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        phoneNumber:
          row.phoneNumber ||
          row.phone ||
          row.Phone ||
          row.mobile ||
          row.Mobile ||
          row["Phone Number"] ||
          row["phone number"] ||
          row["Mobile Number"] ||
          row["mobile number"] ||
          row.PhoneNumber ||
          null,
        jobTitle: row.jobTitle || null,
        department: row.department || null,
        location: row.location || null,
        contractedHoursPerWeek: row.contractedHoursPerWeek
          ? parseFloat(row.contractedHoursPerWeek)
          : null,
        hourlyRate: row.hourlyRate ? parseFloat(row.hourlyRate) : null,
        payrollNumber: row.payrollNumber || null,
        isEligibleForTronc:
          row.isEligibleForTronc?.toLowerCase() === "true" ||
          row.isEligibleForTronc === "1",
        startDate: row.startDate
          ? new Date(row.startDate).toISOString()
          : new Date().toISOString(),
      });
    }

    return { headers, rows, errors: parseErrors };
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      setErrors(["Please select a CSV file"]);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setImportStats(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const {
        headers,
        rows,
        errors: parseErrors,
      } = parseCSV(event.target.result);
      setPreview(rows.slice(0, 5)); // Show first 5 rows
      setErrors(parseErrors);
      setImportStats({
        total: rows.length + parseErrors.length,
        valid: rows.length,
        invalid: parseErrors.length,
      });
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const { rows, errors: parseErrors } = parseCSV(event.target.result);

      if (rows.length === 0) {
        setErrors(["No valid rows to import"]);
        return;
      }

      try {
        await onImport(rows);
        handleClose();
      } catch (err) {
        setErrors([err.message || "Import failed"]);
      }
    };
    reader.readAsText(file);
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
    setImportStats(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const downloadTemplate = () => {
    const headers = expectedHeaders.join(",");
    const exampleRow =
      "John,Smith,john.smith@example.com,07700900000,Waiter,Service,Main Restaurant,40,12.50,EMP001,true,2024-01-15";
    const csv = `${headers}\n${exampleRow}`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "employee-import-template.csv";
    a.click();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[1200]"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[1201] flex items-center justify-center p-4">
        <div
          className="bg-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-heading font-semibold">
                Import Employees from CSV
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Upload a CSV file to bulk import employees
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-muted rounded-md transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Template Download */}
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3">
                <Icon
                  name="FileText"
                  size={20}
                  className="text-primary mt-0.5"
                />
                <div className="flex-1">
                  <h3 className="font-medium">Download Template</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Download a CSV template with the required columns and an
                    example row.
                  </p>
                  <button
                    onClick={downloadTemplate}
                    className="btn btn-outline btn-sm mt-3"
                  >
                    <Icon name="Download" size={16} />
                    Download Template
                  </button>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="label">Select CSV File</label>
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Icon
                  name="Upload"
                  size={32}
                  className="mx-auto mb-3 text-muted-foreground"
                />
                {file ? (
                  <div>
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">Click to select a file</p>
                    <p className="text-sm text-muted-foreground">
                      or drag and drop
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Import Stats */}
            {importStats && (
              <div className="mb-6 grid grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold font-data">
                    {importStats.total}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </div>
                <div className="p-4 bg-success/10 rounded-lg text-center">
                  <p className="text-2xl font-bold font-data text-success">
                    {importStats.valid}
                  </p>
                  <p className="text-sm text-muted-foreground">Valid</p>
                </div>
                <div className="p-4 bg-error/10 rounded-lg text-center">
                  <p className="text-2xl font-bold font-data text-error">
                    {importStats.invalid}
                  </p>
                  <p className="text-sm text-muted-foreground">Invalid</p>
                </div>
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-error mb-2">
                  Validation Errors
                </h4>
                <div className="bg-error/10 rounded-lg p-4 max-h-40 overflow-y-auto">
                  {errors.map((error, index) => (
                    <p
                      key={index}
                      className="text-sm text-error mb-1 last:mb-0"
                    >
                      � {error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Preview */}
            {preview.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Preview (First 5 rows)</h4>
                <div className="overflow-x-auto border border-border rounded-lg">
                  <table className="table text-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Job Title</th>
                        <th>Department</th>
                        <th>Eligible</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row, index) => (
                        <tr key={index}>
                          <td>
                            {row.firstName} {row.lastName}
                          </td>
                          <td>{row.email}</td>
                          <td>{row.jobTitle || "-"}</td>
                          <td>{row.department || "-"}</td>
                          <td>
                            <span
                              className={`badge ${row.isEligibleForTronc ? "badge-success" : "badge-neutral"}`}
                            >
                              {row.isEligibleForTronc ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Expected Columns */}
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">Expected Columns</h4>
              <div className="flex flex-wrap gap-2">
                {expectedHeaders.map((header) => (
                  <span
                    key={header}
                    className={`text-xs px-2 py-1 rounded ${
                      ["firstName", "lastName", "email"].includes(header)
                        ? "bg-primary/10 text-primary font-medium"
                        : "bg-muted-foreground/10 text-muted-foreground"
                    }`}
                  >
                    {header}
                    {["firstName", "lastName", "email"].includes(header)
                      ? " *"
                      : ""}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                * Required fields
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/50">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              className="btn btn-primary"
              disabled={loading || !file || preview.length === 0}
            >
              {loading ? (
                <>
                  <div className="spinner spinner-sm" />
                  Importing...
                </>
              ) : (
                <>
                  <Icon name="Upload" size={18} />
                  Import {preview.length} Employee
                  {preview.length !== 1 ? "s" : ""}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CsvImportModal;
